/**
 * A question's categorization, as one object.
 *
 * Every surface that shows or changes a question's categories (the bank
 * column, the editor, the question sheet, the pickers) asks this object
 * instead of working it out again: which categories the question carries,
 * which course it belongs to, which categories that course offers, how each
 * reads as a path, and what the list becomes after an add or a remove. A rule
 * lives here once; a surface only presents it.
 *
 * It is immutable. Changing the categories returns the next id list; the
 * caller owns saving it.
 */

import { categoryIdsFromTopic } from "@/lib/blueprint/mapping"

import { categoriesInDimension, type CategoryDimension } from "./dimensions"
import { categoryIdsForCourse, courseOfFolder } from "./course"
import { excludedForCourse, type CourseAssignments } from "./course-assignment"
import { buildCategoryTree, getCategoryBreadcrumb } from "./tree"
import type { ExamCategory, ExamCategoryTreeNode } from "./types"

type FolderLike = { id: string; name: string; parentId: string | null }

export type CategorizableQuestion = {
  topic?: string | null
  categoryIds?: readonly string[] | null
  categoryUnassigned?: boolean
  folderId?: string
  folderIds?: readonly string[]
}

export type CategoryEntry = {
  category: ExamCategory
  /** Course, then every ancestor, then the category itself. */
  fullPath: string
  /** Everything above the category: where it sits. */
  parentPath: string
}

export class QuestionCategorization {
  private constructor(
    /** Every category in the program. */
    readonly allCategories: readonly ExamCategory[],
    /** The standard a question is tagged against, when one is defined. */
    readonly dimension: CategoryDimension | undefined,
    private readonly folders: readonly FolderLike[],
    private readonly question: CategorizableQuestion,
    private readonly assignments?: CourseAssignments,
  ) {}

  static of(
    question: CategorizableQuestion,
    context: {
      categories: readonly ExamCategory[]
      dimension?: CategoryDimension
      folders?: readonly FolderLike[]
      /** Which courses each top-level category applies to. */
      assignments?: CourseAssignments
    },
  ): QuestionCategorization {
    return new QuestionCategorization(
      context.categories,
      context.dimension,
      context.folders ?? [],
      question,
      context.assignments,
    )
  }

  /** What the question carries: explicit ids, else the legacy topic. */
  get ids(): string[] {
    if (this.question.categoryUnassigned) return []
    const explicit = (this.question.categoryIds ?? []).filter((id) => id.trim())
    return explicit.length > 0 ? [...explicit] : categoryIdsFromTopic(this.question.topic)
  }

  /** The course this question belongs to, if its folder sits in one. */
  get course(): FolderLike | undefined {
    const folderId = this.question.folderIds?.[0] ?? this.question.folderId
    return courseOfFolder(this.folders, folderId)
  }

  /** Where every path starts: the course, else the standard's name. */
  get rootLabel(): string {
    return this.course?.name ?? this.dimension?.name ?? ""
  }

  /** The categories a person may choose from for this question. */
  get options(): ExamCategory[] {
    const own = this.dimension
      ? categoriesInDimension([...this.allCategories], this.dimension.id)
      : [...this.allCategories]
    const courseId = this.course?.id
    if (this.assignments) {
      // What Settings says applies to this course. If that leaves nothing, the
      // course is one the setting does not describe yet: offer the whole tree.
      const excluded = excludedForCourse(this.allCategories, this.assignments, courseId)
      const kept = own.filter((category) => !excluded.has(category.id))
      return kept.length > 0 ? kept : own
    }
    const scope = categoryIdsForCourse(this.folders, this.allCategories, courseId)
    if (scope.length === 0) return own
    const keep = new Set(scope)
    return own.filter((category) => keep.has(category.id))
  }

  get optionTree(): ExamCategoryTreeNode[] {
    return buildCategoryTree(this.options)
  }

  /** The chosen categories that belong to this standard, in order, with paths. */
  get entries(): CategoryEntry[] {
    const lookup = new Map(this.allCategories.map((category) => [category.id, category]))
    const inStandard = this.dimension
      ? new Set(
          categoriesInDimension([...this.allCategories], this.dimension.id).map((c) => c.id),
        )
      : null
    return this.ids
      .filter((id) => !inStandard || inStandard.has(id))
      .map((id) => lookup.get(id))
      .filter((category): category is ExamCategory => Boolean(category))
      .map((category) => {
        const trail = getCategoryBreadcrumb([...this.allCategories], category.id)
        const join = (nodes: readonly { name: string }[]) =>
          [this.rootLabel, ...nodes.map((node) => node.name)].filter(Boolean).join(" › ")
        return {
          category,
          fullPath: join(trail),
          parentPath: join(trail.slice(0, -1)),
        }
      })
  }

  get names(): string[] {
    return this.entries.map((entry) => entry.category.name)
  }

  get isEmpty(): boolean {
    return this.entries.length === 0
  }

  /** The id list after adding, without duplicates. */
  with(categoryId: string): string[] {
    return [...new Set([...this.ids, categoryId])]
  }

  /** The id list after removing one. */
  without(categoryId: string): string[] {
    return this.ids.filter((id) => id !== categoryId)
  }

  /** The id list after the picker returns its selection for this question's options. */
  replacingSelection(picked: readonly string[]): string[] {
    const inScope = new Set(this.options.map((category) => category.id))
    return [...this.ids.filter((id) => !inScope.has(id)), ...picked]
  }

  /** The chosen ids the picker should show as selected. */
  get selectedOptionIds(): string[] {
    const inScope = new Set(this.options.map((category) => category.id))
    return this.ids.filter((id) => inScope.has(id))
  }
}
